import { api } from '@mbfab/client-api'
import { isNull, isSome } from '@mbfab/shared'
import { createFileRoute, Outlet, useNavigate } from '@tanstack/react-router'

import { CenteredLoading } from '@/components/app/centered-loading'
import { ErrorPanel } from '@/components/app/error-panel'
import { classifyGraphqlError } from '@/lib/apollo/error-classification'
import { AdminIdentityProvider } from '@/providers/admin-identity-context'
import { AuthenticatedApolloProvider } from '@/providers/authenticated-apollo-provider'

// admin 配下は特定テナントに紐付かないため tenantId は null
export const Route = createFileRoute('/admin')({
  component: () => (
    <AuthenticatedApolloProvider tenantId={null}>
      <AdminGate />
    </AuthenticatedApolloProvider>
  ),
})

// v1 の layouts/AdminLayout.tsx と同じロールガード。
// バックエンドの admin 系 resolver は throwIfNotAuthorizedOrNotAdmin で守られているので
// ここの目的は防御ではなく、権限が無いことを明示して行き止まりにしないこと
function AdminGate() {
  const navigate = useNavigate()
  const identityQuery = api.useFetchMyIdentityQuery()

  if (identityQuery.loading) {
    return <CenteredLoading message="読み込み中..." />
  }

  if (isSome(identityQuery.error)) {
    if (classifyGraphqlError(identityQuery.error) === 'unauthenticated') {
      return (
        <ErrorPanel
          title="ログインの有効期限が切れました"
          description="お手数ですが、もう一度ログインしてください。"
          action={{
            label: 'ログインし直す',
            onClick: () => {
              void navigate({ to: '/sign_out' })
            },
          }}
        />
      )
    }
    return <ErrorPanel title="読み込みに失敗しました" description="しばらくしてから再度お試しください。" />
  }

  const myIdentity = identityQuery.data?.myIdentity
  if (isNull(myIdentity)) {
    return <CenteredLoading message="読み込み中..." />
  }

  if (myIdentity.role !== 'admin') {
    return (
      <ErrorPanel
        title="このページにはアクセスできません"
        description="管理者用メニューは Mill-Box の運営メンバーのみが利用できます。"
        action={{
          label: 'テナント選択に戻る',
          onClick: () => {
            void navigate({ to: '/' })
          },
        }}
      />
    )
  }

  return (
    <AdminIdentityProvider myIdentity={myIdentity}>
      <Outlet />
    </AdminIdentityProvider>
  )
}
